import { forwardRef, useCallback, useState } from 'react';

import Typography from 'components/global/Typography';

import useUpdateEffect from 'hooks/useUpdateEffect';

import { faStar as faStarRegular } from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';

import * as S from './styled/ScoreCard.styled';

/**
 * ScoreCard component to display and optionally update a score using star icons.
 * @param {Object} props - The props for the component.
 * @param  {'star' | 'nps'} [props.variant="star"] - Variant value
 * @param {number} [props.value = 2] - Score value.
 * @param {Function} [props.setValue] - Function to update the score value.
 * @param {number} [props.base = 5] - Maximum/base score value.
 * @param {boolean} [props.disabled] - Whether the score is disabled.
 * @param {'x-small' | 'small' | 'medium' | 'large' | 'x-large' | 'xx-large' | 'xxx-large' } [props.size = "x-small"] - Size value
 * @param {boolean} [props.showText = true] - Whether to show the score value.
 * @param {string} [props.textColor] - Text color.
 * @example
 * import Button from 'components/field/ScoreCard';
 *
 * <ScoreCard
 *   value={value}
 *   setValue={setValue}
 * />
 */

const ScoreCard = (props, ref) => {
	const {
		variant = 'star',
		value,
		setValue,
		size = 'x-small',
		base = 5,
		disabled,
		showText = true,
		textColor
	} = props;
	const [currentScore, setCurrentScore] = useState(value ? value.toFixed(1) : '0.0');

	useUpdateEffect(() => {
		const newValue = value ? value.toFixed(1) : '0.0';
		if (newValue !== currentScore) {
			setCurrentScore(newValue);
		}
	}, [value]);

	const solidStars = Math.floor(currentScore);
	const fraction = Number(String(currentScore).split('.')[1] ?? 0);

	const getColor = useCallback(
		(score) => {
			const percentage = (score / base) * 100;
			if (percentage <= 60) return 'red';
			if (percentage <= 80) return 'yellow';
			return 'green';
		},
		[base]
	);

	const handleClick = (clickedScore) => {
		if (!disabled && setValue) {
			setValue(clickedScore);
			setCurrentScore(clickedScore.toFixed(1));
		}
	};

	return (
		<S.ScoreCardWrapper>
			<S.ScoreCard ref={ref} className={`${variant === 'star' ? size : ''}`}>
				<S.ScoreStars className={`${variant === 'star' ? size : ''}`}>
					{[...Array(base)].map((_, index) =>
						variant === 'nps' ? (
							index === solidStars && fraction !== 0 ? (
								<S.FractionalStarContainer
									key={index}
									onClick={() => handleClick(index + 1)}
									disabled={disabled}></S.FractionalStarContainer>
							) : (
								<S.ScoreNps
									className={`${disabled ? 'disabled' : ''} ${index + 1 <= currentScore ? 'selected' : ''}`}
									key={index}
									onClick={() => handleClick(index + 1)}
									background={getColor(index + 1)}
									selectedBackground={getColor(currentScore)}>
									<Typography fontWeight="700">{index + 1}</Typography>
								</S.ScoreNps>
							)
						) : index === solidStars && fraction !== 0 ? (
							<S.FractionalStarContainer
								key={index}
								onClick={() => handleClick(index + 1)}
								disabled={disabled}>
								<S.StyledIcon icon={faStarSolid} percent={fraction} disabled={disabled} size={size} />
								<S.StyledIcon icon={faStarRegular} disabled={disabled} size={size} />
							</S.FractionalStarContainer>
						) : (
							<S.StyledIcon
								key={index}
								icon={index < solidStars ? faStarSolid : faStarRegular}
								onClick={() => handleClick(index + 1)}
								disabled={disabled}
								size={size}
							/>
						)
					)}
				</S.ScoreStars>
				{showText && (
					<Typography color={textColor} fontWeight="500">
						{currentScore}
					</Typography>
				)}
			</S.ScoreCard>

			{variant === 'nps' && (
				<S.RateDescription>
					<Typography>notLikeAtAll</Typography>
					<Typography>extremelyLikely</Typography>
				</S.RateDescription>
			)}
		</S.ScoreCardWrapper>
	);
};

/**
 * ScoreCard component to display and optionally update a score using star icons.
 * @param {Object} props - The props for the component.
 * @param  {'star' | 'nps'} [props.variant="star"] - Variant value
 * @param {number} [props.value = 2] - Score value.
 * @param {Function} [props.setValue] - Function to update the score value.
 * @param {number} [props.base = 5] - Maximum/base score value.
 * @param {boolean} [props.disabled] - Whether the score is disabled.
 * @param {boolean} [props.showValue = true] - Whether to show the score value.
 * @param {'x-small' | 'small' | 'medium' | 'large' | 'x-large' | 'xx-large' } [props.size = "x-small"] - Size value
 * @example
 * import Button from 'components/field/ScoreCard';
 *
 * <ScoreCard
 *   value={value}
 *   setValue={setValue}
 * />
 */
export default forwardRef(ScoreCard);
